import * as React from 'react';
import { connect } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import sanitizeHtml from 'sanitize-html';

import PageSection from 'components/page/page-section/PageSection';
import { Table, TableCell, TableContainer, TableHead, TableRow } from 'components/table';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Spinner from 'components/spinner/Spinner';
import Overlay from 'components/overlay/Overlay';

import { fetchSubscriptionByOrganizationId } from 'modules/subscription/reducers/subscription';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { isAdmin } from 'utils/account';
import { isCcmOnlyFocusedOrganization } from 'utils/organization';
import { sanitizeGenericHtml } from 'utils/sanitize';

import './organization-subscriptions.scss';

applyTranslations(require('./organization-subscriptions.i18n.json'));

import type {
  Account,
  Organization,
  Role,
  SubscriptionAgreements,
  SubscriptionProductAgreement,
  SubscriptionProductAgreements,
  SubscriptionType
} from 'types';

type OwnProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  organizationId: number // used in mapDispatchToProps
};

type StateProps = {
  organization: Organization,
  account: Account,
  roles: Array<Role>,
  product: {
    type: SubscriptionType,
    agreements: Array<SubscriptionAgreements[number] | 'ccm'>,
    productAgreements: SubscriptionProductAgreements & { ccm: SubscriptionProductAgreement }
  },
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchSubscription: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['strong']
};

const MSG_KEY = 'organization_subscriptions';

class OrganizationSubscriptions extends React.PureComponent<Props> {
  componentDidMount() {
    const { dispatchFetchSubscription } = this.props;

    dispatchFetchSubscription();
  }

  getAgreementInformation(productAgreement: SubscriptionProductAgreement) {
    const isCcmProductAgreement = productAgreement.agreedCommission === 0
      && productAgreement.agreedCommissionPercentage === 0
      && productAgreement.agreedMonthlyFee === 0
      && productAgreement.capabilities.length === 0
      && productAgreement.isTrial === false
      && productAgreement.trialFrom === ''
      && productAgreement.trialUntil === '';
    let information;

    if (isCcmProductAgreement) {
      information = __t(`${MSG_KEY}.messages.ccm`);
    } else {
      information = productAgreement.isTrial ? (
        __t(`${MSG_KEY}.messages.trial_from_until`, {
          from: getDisplayDate(productAgreement.trialFrom),
          until: getDisplayDate(productAgreement.trialUntil)
        })
      ) : (
        __t(`${MSG_KEY}.messages.monthly_fee_and_commission`, {
          fee: productAgreement.agreedMonthlyFee,
          commission: productAgreement.agreedCommission * 100
        })
      );
    }

    return { __html: sanitizeGenericHtml(information) };
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="organization-subscriptions__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderExploreBox(): React.ReactNode {
    return (
      <div className="organization-subscriptions__body">
        <div>
          {__t(`${MSG_KEY}.messages.change_subscription`)}
        </div>
        <div>
          {__t(`${MSG_KEY}.labels.get_in_touch`)}&nbsp;
          <a
            href="mailto:hello@collabary.com"
            className="organization-subscriptions__link"
          >
            hello@collabary.com
          </a>
        </div>
        {isCcmOnlyFocusedOrganization(this.props.organization) ? null : (
          <div className="organization-subscriptions__button">
            <ActionButton
              color="tertiary"
              width="minimum"
              link="https://www.collabary.com/pricing/"
            >
              {__t(`${MSG_KEY}.labels.explore_plans`)}
            </ActionButton>
          </div>
        )}
      </div>
    );
  }

  renderTableHead(): React.ReactNode {
    return (
      <TableRow className="organization-subscriptions__table-row">
        <TableHead width="fill">
          {__t(`${MSG_KEY}.labels.product`)}
        </TableHead>
        <TableHead width="small">
          {__t(`${MSG_KEY}.labels.package`)}
        </TableHead>
      </TableRow>
    );
  }

  renderOverlay(): React.ReactNode {
    const { organization, account, roles } = this.props;

    const freemiumMessage = __t(`${MSG_KEY}.messages.is_freemium`, {
      date: getDisplayDate(organization.freemiumUntil)
    });

    return (
      <Overlay
        parentId="product-overlay"
        withDefaultCursor
      >
        <div className="organization-subscriptions__overlay">
          <div className="organization-subscriptions__overlay-header">
            {organization.name}
          </div>
          <div className="organization-subscriptions__overlay-body">
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(freemiumMessage, SANITIZE_HTML_CONFIG) }} />
            {
              isAdmin(account, roles) && (
                <div>
                  <span>
                    {__t(`${MSG_KEY}.messages.update_freemium`)}
                  </span>
                  <a
                    href={`/admin/organizations/${organization.id}/subscription`}
                    className="organization-subscriptions__overlay-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {__t(`${MSG_KEY}.labels.click_here`)}
                  </a>.
                </div>
              )
            }
          </div>
        </div>
      </Overlay>
    );
  }

  renderProductAgreements(): React.ReactNode {
    const { organization, product } = this.props;

    return product.agreements.sort().map(agreement => (
      <TableRow
        key={agreement}
        className="organization-subscriptions__table-row"
        align="top"
      >
        <TableCell width="fill">
          <div
            id="product-overlay"
            className="organization-subscriptions__product-label"
          >
            {__t(`${MSG_KEY}.labels.${agreement}`)}
            <div
              className="organization-subscriptions__product-label--small"
              dangerouslySetInnerHTML={this.getAgreementInformation(product.productAgreements[agreement])}
            />
            {
              organization.isFreemium && this.renderOverlay()
            }
          </div>
        </TableCell>
        <TableCell width="small">
          {__t(`${MSG_KEY}.labels.${product.type}`)}
        </TableCell>
      </TableRow>
    ));
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;

    return (
      <React.Fragment>
        <PageSection
          title={__t(`${MSG_KEY}.labels.subscriptions`)}
          style="none"
          padding="none"
          margin="none"
          titleProps={{ margin: 'top-none' }}
        >
          <TableContainer>
            <Table style="volume">
              <div className="organization-subscriptions__agreements">
                {this.renderTableHead()}
                {
                  isLoading
                    ? this.renderSpinner()
                    : this.renderProductAgreements()
                }
              </div>
            </Table>
          </TableContainer>
        </PageSection>
        <PageSection padding="none">
          {this.renderExploreBox()}
        </PageSection>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ account, organizations, subscription }): StateProps => ({
  organization: organizations.currentOrganization,
  account: account.currentAccount,
  roles: account.roles,
  product: (() => {
    const product = cloneDeep(subscription.product);
    const ccmStatus = organizations?.currentOrganization?.ccmStatus;

    if (ccmStatus !== 'disabled') {
      product.agreements = ccmStatus === 'only'
        ? ['ccm']
        : [...product.agreements, 'ccm'];

      product.productAgreements.ccm = {
        agreedCommission: 0,
        agreedCommissionPercentage: 0,
        agreedMonthlyFee: 0,
        capabilities: [],
        isTrial: false,
        trialFrom: '',
        trialUntil: ''
      };
    }

    return product;
  })(),
  isLoading: subscription.isLoading
});

const mapDispatchToProps = (dispatch, ownProps: OwnProps): DispatchProps => ({
  dispatchFetchSubscription: () => {
    dispatch(fetchSubscriptionByOrganizationId(ownProps.organizationId));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationSubscriptions);
